import { type JSX,useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
    Button,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { setCurrentEntityType, setDialogOpen } from 'common/components/deleteDialog/deleteDialogSlice';
import { NotFound } from 'common/components/NotFound';
import { Page } from 'common/components/Page';
import { ResourceDetailHeader } from 'common/components/ResourceDetailHeader';
import { StyledBreadcrumbs } from 'common/components/StyledBreadcrumbs';
import { Roles } from 'common/constants/roles';
import { Routes, RoutesNameMap } from 'common/constants/routes';
import { useAuthContext } from 'common/contexts/AuthContext';
import { is404Error, isSomethingWentWrongError } from 'common/services/api/apiHelpers';
import { fetchUserDetails } from 'common/services/api/Users/Users.thunk';
import { UserDetails } from 'common/types/Entities';
import { DrawerEntitiesNames } from 'common/types/TypeDefs';
import { disableIfMissingAnyRole } from 'common/utils/DisableButtonEvaluator';
import { UserProfileApps } from 'features/userProfileApps/UserProfileApps';
import { UserProfileForeignSystems } from 'features/userProfileForeignSystems/UserProfileForeignSystems';
import { UserProfileIdentities } from 'features/userProfileIdentities/UserProfileIdentities';
import { selectUserDetails, setUserDetails } from 'features/users/usersSlice';

import { UserDetailsDeleteDialog } from './UserDetailsDeleteDialog';
import { UserProfileContentWrapper } from './UserProfileContentWrapper';

const APPS_AND_TENANTS_TITLE = 'Application Suites And Tenants';

export function UserProfileDetails(): JSX.Element {

    const [error, setError] = useState<string>('');
    const [processing, setProcessing] = useState<boolean>(true);
    const params = useParams();
    const userDetails = useAppSelector(selectUserDetails);

    const { currentUserRoles } = useAuthContext();
    const allowedRoles = [Roles.UserManager, Roles.Admin, Roles.AppManager, Roles.MultiTenantRO];
    const shouldDisableUserAppsTenants = disableIfMissingAnyRole(currentUserRoles, allowedRoles);
    const allowedDeleteUserRoles = [Roles.UserManager, Roles.Admin];
    const shouldDisableDeleteUser = disableIfMissingAnyRole(currentUserRoles, allowedDeleteUserRoles);

    const dispatch = useAppDispatch();

    useEffect(() => {
        const fetchUserDetailsParams = {
            userId: params?.userId ?? ''
        };
        dispatch(fetchUserDetails(fetchUserDetailsParams))
            .unwrap()
            .then((result) => {
                dispatch(setUserDetails(result));
            })
            .catch((err) => {
                setError(err.message);
            })
            .finally(() => {
                setProcessing(false);
            });
    }, []);

    if (is404Error(error)) {
        return <NotFound />;
    }

    if (isSomethingWentWrongError(error)) {
        return <p>Something went wrong</p>;
    }

    if(processing) {
        return <CircularProgress color='primary'/>;
    }

    const getUserFullName = (userDetailsParam: UserDetails | null): string => {
        const UNKNOWN_USER_DEFAULT_FULL_NAME = 'Unknown User';
        const { firstName='', lastName='' } = userDetailsParam ?? {};
        const existingUserFullName = `${firstName} ${lastName}`;
        return firstName && lastName ? existingUserFullName : UNKNOWN_USER_DEFAULT_FULL_NAME;
    };

    const buildPageTitle = (): string => {
        const userFullName = getUserFullName(userDetails);
        return `${userFullName} - ${RoutesNameMap.get(Routes.UsersDetails)}`;
    };

    const handleDeleteButtonClick = () => {
        dispatch(setDialogOpen(true));
        dispatch(setCurrentEntityType(DrawerEntitiesNames.UserProfile));
    };

    return (
        <Page
            title={buildPageTitle()}
            header={(
                <Table>
                    <TableBody>
                        <TableRow sx={{ '& td': { border: 0 } }}>
                            <TableCell align="left">
                                <Typography variant="h5" gutterBottom component="div">
                                    {userDetails?.userId &&
                                        <StyledBreadcrumbs
                                            numberOfTabsToExcludeFromLinks={1}
                                            guidNameMappings={[{
                                                resourceId: params.userId ?? '',
                                                resourceName: getUserFullName(userDetails)
                                            }]}
                                        />}
                                    {userDetails?.userId &&
                                        <ResourceDetailHeader
                                            resourceType='User ID'
                                            resourceName={getUserFullName(userDetails)}
                                            resourceId={userDetails?.userId || ''}
                                            useDefaultResourceTypeTitle={false}
                                        />
                                    }
                                </Typography>
                            </TableCell>
                            <TableCell align="right" >
                                <Button
                                    sx={{
                                        fontFamily: ['Source Sans Pro'].join(','),
                                        WebkitFontSmoothing: 'auto',
                                        letterSpacing: 'normal',
                                        textTransform: 'capitalize',
                                        fontSize: 16,
                                    }}
                                    data-testid='delete-user-button'
                                    variant="text"
                                    onClick={handleDeleteButtonClick}
                                    disabled={shouldDisableDeleteUser}
                                >
                                    Delete User
                                </Button>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            )}
        >
            <UserProfileContentWrapper>
                <UserProfileForeignSystems />
                {userDetails?.userId &&
                    <UserProfileIdentities />
                }
            </UserProfileContentWrapper>
            <UserDetailsDeleteDialog
                editingRow={{
                    userFullName: getUserFullName(userDetails),
                    userId: params.userId ?? ''
                }}/>
            {!shouldDisableUserAppsTenants && <UserProfileApps title={APPS_AND_TENANTS_TITLE}/>}
        </Page>
    );
}
